import React, { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { GET_TEAM_AVAILABILITY_TAPE_CHART, GET_TEAM_PTO_OVERVIEW } from '../../graphql/team-availability-queries';
import { GET_USERS } from '../../graphql/queries';
import EditScheduleModal from './EditScheduleModal';
import VacationFormModal from './VacationFormModal';
import AdminEditUserAvailabilityPanel from './admin-edit/AdminEditUserAvailabilityPanel';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const SCHEDULE_COLORS = {
  WORKING: 'bg-blue-500',
  WORKING_REMOTE: 'bg-purple-500',
  OFF: 'bg-gray-200',
  HOLIDAY: 'bg-red-500',
  VACATION: 'bg-yellow-400',
  NOT_SET: 'bg-white border-2 border-dashed border-gray-300',
};

const SCHEDULE_TEXT_COLORS = {
  WORKING: 'text-white',
  WORKING_REMOTE: 'text-white',
  OFF: 'text-gray-600',
  HOLIDAY: 'text-white',
  VACATION: 'text-gray-800',
  NOT_SET: 'text-gray-400',
};

const AVAILABLE_ROLES = [
  { value: 'ADMIN', label: 'Admin', enabledByDefault: true },
  { value: 'HOUSEKEEPER_MANAGER', label: 'Housekeeper Manager', enabledByDefault: true },
  { value: 'PROPERTY_MANAGER', label: 'Property Manager', enabledByDefault: true },
  { value: 'INSPECTOR', label: 'Inspector', enabledByDefault: true },
  { value: 'MAINTENANCE', label: 'Maintenance', enabledByDefault: true },
  { value: 'CALL_CENTER', label: 'Concierge / Call Center', enabledByDefault: true },
  { value: 'HOUSEKEEPER', label: 'Housekeeper', enabledByDefault: false },
];

const ROLE_LABELS = AVAILABLE_ROLES.reduce((labels, role) => {
  labels[role.value] = role.label;
  return labels;
}, {});

const DEFAULT_SELECTED_ROLES = AVAILABLE_ROLES
  .filter((role) => role.enabledByDefault)
  .map((role) => role.value);

const AREA_LABELS = {
  KIAWAH: 'Kiawah',
  ISLE_OF_PALMS: 'Isle of Palms',
  ISLE_OF_PALMS_WILD_DUNES: 'Wild Dunes',
  SEABROOK_ISLAND: 'Seabrook',
};

// Area groups - the four underlying LocationPreference values are bundled
// into the two operational regions managers actually think in.
const AREA_GROUPS = {
  IOP: {
    value: 'IOP',
    label: 'Isle of Palms',
    areas: ['ISLE_OF_PALMS', 'ISLE_OF_PALMS_WILD_DUNES'],
  },
  KIAWAH: {
    value: 'KIAWAH',
    label: 'Kiawah',
    areas: ['KIAWAH', 'SEABROOK_ISLAND'],
  },
};

const AREA_GROUP_OPTIONS = [
  { value: AREA_GROUPS.IOP.value, label: 'Isle of Palms + Wild Dunes' },
  { value: AREA_GROUPS.KIAWAH.value, label: 'Kiawah + Seabrook' },
];

const deriveDefaultAreaGroups = (userAreas) => {
  if (!Array.isArray(userAreas) || userAreas.length === 0) return [];
  const inIop = userAreas.some((area) => AREA_GROUPS.IOP.areas.includes(area));
  const inKiawah = userAreas.some((area) =>
    AREA_GROUPS.KIAWAH.areas.includes(area),
  );
  return [
    ...(inIop ? [AREA_GROUPS.IOP.value] : []),
    ...(inKiawah ? [AREA_GROUPS.KIAWAH.value] : []),
  ];
};

const formatRoleLabel = (role) => ROLE_LABELS[role] || role?.replace(/_/g, ' ') || 'Role not set';

const buildAreaLabels = (areas) => {
  if (!Array.isArray(areas) || areas.length === 0) return ['No Area'];

  const remainingAreas = new Set(areas);
  const labels = [];

  if (AREA_GROUPS.IOP.areas.every((area) => remainingAreas.has(area))) {
    labels.push('Isle of Palms + Wild Dunes');
    AREA_GROUPS.IOP.areas.forEach((area) => remainingAreas.delete(area));
  }

  if (AREA_GROUPS.KIAWAH.areas.every((area) => remainingAreas.has(area))) {
    labels.push('Kiawah + Seabrook');
    AREA_GROUPS.KIAWAH.areas.forEach((area) => remainingAreas.delete(area));
  }

  remainingAreas.forEach((area) => {
    labels.push(AREA_LABELS[area] || area.replace(/_/g, ' '));
  });

  return labels;
};

const buildScheduleCellLabel = (dayStatus) => {
  if (dayStatus.status === 'HOLIDAY' && dayStatus.holidayName) {
    return dayStatus.holidayName.substring(0, 3);
  }
  if (dayStatus.status === 'VACATION') return 'Vac';
  if (dayStatus.status === 'WORKING' && dayStatus.isRemote === true) return 'R';
  if (dayStatus.status === 'WORKING') return 'W';
  if (dayStatus.status === 'OFF') return 'Off';
  return '\u2014';
};

const buildScheduleCellTitle = (dayStatus, isRemote) => {
  const note = (dayStatus.notes || '').trim();
  const baseTitle =
    dayStatus.holidayName ||
    dayStatus.vacationType ||
    (isRemote ? 'Working Remote' : dayStatus.status);
  return note ? `${baseTitle}\nNote: ${note}` : baseTitle;
};

function FilterDropdown({ label, options, selectedValues, onToggle, onClear }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOptions = options.filter((option) => selectedValues.includes(option.value));
  const summary = selectedOptions.length === 0
    ? `All ${label.toLowerCase()}`
    : selectedOptions.length === 1
      ? selectedOptions[0].label
      : `${selectedOptions.length} selected`;

  return (
    <div className="relative" ref={dropdownRef}>
      <span className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </span>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
      >
        <span className={`truncate ${selectedOptions.length === 0 ? 'text-gray-500' : 'font-medium'}`}>
          {summary}
        </span>
        <svg
          className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label={`${label} filters`}
          aria-multiselectable="true"
          className="absolute z-30 mt-1 w-full min-w-[220px] max-h-64 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5"
        >
          <button
            type="button"
            role="option"
            aria-selected="false"
            onClick={() => {
              onClear();
            }}
            disabled={selectedValues.length === 0}
            className="w-full flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-left text-sm font-medium text-sky-700 hover:bg-sky-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-white"
          >
            <span>Clear all</span>
            <span className="text-xs font-normal text-gray-500">Then choose one or more</span>
          </button>
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onToggle(option.value)}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                  isSelected ? 'font-medium text-emerald-800' : 'text-gray-700'
                }`}
              >
                <span>{option.label}</span>
                {isSelected && (
                  <span className="text-emerald-700" aria-hidden="true">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ScheduleDayCell({ dayStatus }) {
  const isRemote = dayStatus.status === 'WORKING' && dayStatus.isRemote === true;
  const displayStatus = isRemote ? 'WORKING_REMOTE' : dayStatus.status;
  const bgColor = SCHEDULE_COLORS[displayStatus] || SCHEDULE_COLORS.OFF;
  const textColor = SCHEDULE_TEXT_COLORS[displayStatus] || SCHEDULE_TEXT_COLORS.OFF;
  const cellLabel = buildScheduleCellLabel(dayStatus);
  const note = (dayStatus.notes || '').trim();
  const hasNote = note.length > 0;
  const cellTitle = buildScheduleCellTitle(dayStatus, isRemote);
  return (
    <div
      className={`${bgColor} ${textColor} relative w-full rounded-md px-3 py-2 text-sm font-semibold min-h-[48px] flex items-center justify-center`}
      title={cellTitle}
    >
      {cellLabel}
      {hasNote && (
        <svg
          className="absolute top-0.5 right-0.5 w-3 h-3 text-white drop-shadow"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-label={`Note: ${note}`}
        >
          <path
            fillRule="evenodd"
            d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h6v2H9V8zm-4 4h2v2H5v-2zm4 0h4v2H9v-2z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </div>
  );
}

function SetupStatusIcon({ isSet, label, title }) {
  return (
    <span
      className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${
        isSet ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
      }`}
      title={title}
      role="img"
      aria-label={`${label}: ${isSet ? 'set' : 'not set'}`}
    >
      {isSet ? (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
    </span>
  );
}

const getDateKey = (value) => {
  if (typeof value === 'string') {
    const match = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (match) return match[0];
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : format(date, 'yyyy-MM-dd');
};

function WorkingStatusBadge({ dayStatus }) {
  let label = 'Not Set';
  let title = 'Today\'s schedule is not set';
  let classes = 'border-gray-200 bg-gray-100 text-gray-600';
  let icon = null;

  if (dayStatus?.status === 'WORKING' && dayStatus.isRemote) {
    label = 'On - Remote';
    title = 'Working remotely today';
    classes = 'border-sky-200 bg-sky-50 text-sky-800';
    icon = (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.5 16.5a5 5 0 017 0M5 13a10 10 0 0114 0M2 9.5a15 15 0 0120 0M12 20h.01" />
      </svg>
    );
  } else if (dayStatus?.status === 'WORKING') {
    label = 'On';
    title = 'Working today';
    classes = 'border-emerald-200 bg-emerald-50 text-emerald-800';
    icon = (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l2.5 2.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  } else if (dayStatus?.status === 'VACATION') {
    label = 'PTO';
    title = dayStatus.vacationType || 'Paid time off today';
    classes = 'border-violet-200 bg-violet-50 text-violet-800';
    icon = (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 100 100" aria-hidden="true">
        <path d="m14.602 32.301 30.398 13.898-9.6992 9.6992-14.699-2.3008c-0.39844-0.10156-0.80078 0.10156-1.1016 0.30078l-4.6992 4.6992c-0.30078 0.30078-0.39844 0.69922-0.39844 1 0.10156 0.39844 0.30078 0.69922 0.60156 0.89844l15.699 8.8984 8.3984 15.199c0.19922 0.30078 0.5 0.60156 0.89844 0.60156 0.39844 0.10156 0.80078-0.10156 1-0.30078l5.3984-5.3984c0.30078-0.30078 0.39844-0.69922 0.30078-1.1016l-2.8008-13.699 9.6992-9.6992 13.801 30.199c0.19922 0.39844 0.5 0.60156 0.89844 0.69922h0.19922c0.30078 0 0.60156-0.10156 0.89844-0.39844l4.8984-4.8984c0.30078-0.30078 0.39844-0.69922 0.30078-1.1016l-7.3984-38.199 15.301-15.301c2.3008-2.3008 4.6992-8.3008 2.1016-10.898-1.1992-1.1992-3.3008-1.5-5.8008-0.69922-2 0.60156-4 1.6992-5.1016 2.8008l-14.895 15.199-38.5-7.3984c-0.39844-0.10156-0.80078 0-1.1016 0.30078l-4.8984 4.8984c-0.30078 0.30078-0.39844 0.69922-0.30078 1.1016 0.10156 0.39844 0.30078 0.80078 0.60156 1z" />
      </svg>
    );
  } else if (dayStatus?.status === 'OFF' || dayStatus?.status === 'HOLIDAY') {
    label = 'Off';
    title = dayStatus.holidayName || 'Off today';
    classes = 'border-rose-200 bg-rose-50 text-rose-700';
    icon = (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 100 100" aria-hidden="true">
        <path d="m50 8.332c-11.051 0-21.648 4.3906-29.461 12.207-7.8164 7.8125-12.207 18.41-12.207 29.461s4.3906 21.648 12.207 29.461c7.8125 7.8164 18.41 12.207 29.461 12.207s21.648-4.3906 29.461-12.207c7.8164-7.8125 12.207-18.41 12.207-29.461-0.011719-11.047-4.4062-21.637-12.219-29.449s-18.402-12.207-29.449-12.219zm-33.332 41.668c-0.015625-8.3594 3.1172-16.418 8.7734-22.574s13.426-9.957 21.754-10.645c8.332-0.69141 16.617 1.7812 23.211 6.9219l-46.703 46.703c-4.5586-5.8242-7.0352-13.008-7.0352-20.406zm33.332 33.332c-7.3984 0-14.582-2.4766-20.406-7.0352l46.703-46.703c5.1406 6.5938 7.6133 14.879 6.9219 23.211-0.6875 8.3281-4.4883 16.098-10.645 21.754s-14.215 8.7891-22.574 8.7734z" />
      </svg>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold ${classes}`}
      title={title}
    >
      {icon}
      {label}
    </span>
  );
}

function EmployeeRow({
  employee,
  weekDays,
  isExpanded,
  onToggle,
  onEditWeek,
  onEditOffDates,
  onEditAvailability,
  onPreviousWeek,
  onNextWeek,
  canManageAvailability,
  isAdmin,
  canViewPtoBalance,
}) {
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const todayStatus = employee.days.find((dayStatus) => getDateKey(dayStatus.date) === todayKey);
  const areaLabel = buildAreaLabels(employee.employeeAreas).join(', ');
  const scheduleIsSet = employee.hasCompleteWeeklySchedule === true;
  const locationIsSet = Boolean(employee.coverageUserId && employee.coverageUserName);
  const handleHeaderKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };
  return (
    <>
      <tr
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`${employee.employeeName} status, click to ${isExpanded ? 'collapse' : 'expand'} schedule`}
        onClick={onToggle}
        onKeyDown={handleHeaderKeyDown}
        className="border-b border-gray-200 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500 cursor-pointer transition-colors"
      >
        <td className="px-4 py-4 font-medium text-gray-900 whitespace-nowrap">
          {employee.employeeName}
        </td>
        <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
          {formatRoleLabel(employee.employeeRole)}
        </td>
        <td className="px-4 py-4 text-sm text-gray-700 min-w-[190px]">
          {areaLabel}
        </td>
        <td className="px-4 py-4 text-center">
          <SetupStatusIcon
            isSet={scheduleIsSet}
            label="Schedule"
            title={scheduleIsSet ? 'Weekly schedule set' : `Schedule ${employee.weeklyScheduleDaysSet || 0}/7 days set`}
          />
        </td>
        <td className="px-4 py-4 text-center">
          <SetupStatusIcon
            isSet={locationIsSet}
            label="Location"
            title={locationIsSet ? `Coverage: ${employee.coverageUserName}` : 'No coverage contact selected'}
          />
        </td>
        <td className="px-4 py-4">
          <WorkingStatusBadge dayStatus={todayStatus} />
        </td>
        <td className="pl-2 pr-6 py-4 text-right">
          <svg
            className={`w-5 h-5 text-gray-400 inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b border-gray-200">
          <td colSpan="7" className="px-4 pb-5 pt-4 bg-gray-50">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Week schedule
                  </div>
                  {canViewPtoBalance && (
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${
                      employee.ptoDaysRemaining == null
                        ? 'border-gray-200 bg-white text-gray-600'
                        : 'border-violet-200 bg-violet-50 text-violet-800'
                    }`}>
                      PTO remaining: {employee.ptoDaysRemaining == null
                        ? 'Not Set'
                        : `${employee.ptoDaysRemaining} ${employee.ptoDaysRemaining === 1 ? 'day' : 'days'}`}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-self-center gap-2">
                  <button
                    type="button"
                    onClick={onPreviousWeek}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:border-sky-200 hover:bg-sky-50 hover:text-sky-800"
                    aria-label="Show previous week"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous Week
                  </button>
                  <button
                    type="button"
                    onClick={onNextWeek}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:border-sky-200 hover:bg-sky-50 hover:text-sky-800"
                    aria-label="Show next week"
                  >
                    Next Week
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <div className="lg:justify-self-end">
                  {isAdmin ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditAvailability();
                      }}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-sky-600 hover:bg-sky-700 border border-sky-600 rounded-md transition-colors whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-sky-500"
                      aria-label={`Edit availability for ${employee.employeeName}`}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit Availability
                    </button>
                  ) : (
                    canManageAvailability && (
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditWeek();
                          }}
                          className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200 rounded-md transition-colors whitespace-nowrap"
                        >
                          Edit Week
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditOffDates();
                          }}
                          className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200 rounded-md transition-colors whitespace-nowrap"
                        >
                          Edit Off Dates
                        </button>
                      </div>
                    )
                  )}
                </div>
              </div>
              <div className="grid grid-cols-7 gap-2">
                {employee.days.map((dayStatus, idx) => {
                  const dayDate = weekDays[idx] || new Date(dayStatus.date);
                  const isToday = getDateKey(dayStatus.date) === todayKey;
                  return (
                    <div
                      key={idx}
                      className={`min-h-[126px] rounded-lg border bg-white p-3 flex flex-col gap-3 ${
                        isToday
                          ? 'border-sky-400 ring-2 ring-sky-100 shadow-sm'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className={`text-xs font-semibold uppercase tracking-wide ${
                            isToday ? 'text-sky-700' : 'text-gray-500'
                          }`}>
                            {DAY_NAMES[dayDate.getDay()]}
                          </div>
                          <div className="mt-0.5 text-lg font-semibold text-gray-900">
                            {format(dayDate, 'MMM d')}
                          </div>
                        </div>
                        {isToday && (
                          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                            Today
                          </span>
                        )}
                      </div>
                      <div className="mt-auto">
                        <ScheduleDayCell dayStatus={dayStatus} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function CalendarAvailabilityView({ employees, weekDays }) {
  const todayKey = format(new Date(), 'yyyy-MM-dd');

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1180px] table-fixed">
        <colgroup>
          <col className="w-[250px]" />
          {weekDays.map((day) => <col key={day.toISOString()} />)}
        </colgroup>
        <thead className="border-b border-gray-200 bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Team Member
            </th>
            {weekDays.map((day) => {
              const isToday = format(day, 'yyyy-MM-dd') === todayKey;
              return (
                <th
                  key={day.toISOString()}
                  className={`px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide ${
                    isToday ? 'bg-sky-50 text-sky-700' : 'text-gray-500'
                  }`}
                >
                  <div>{format(day, 'EEE')}</div>
                  <div className="mt-0.5 text-sm normal-case tracking-normal">{format(day, 'MMM d')}</div>
                  {isToday && <div className="mt-1 text-[10px] uppercase tracking-wide">Today</div>}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {employees.map((employee) => (
            <tr key={employee.employeeId} className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50">
              <td className="px-4 py-4 align-top">
                <div className="font-semibold text-gray-900">{employee.employeeName}</div>
                <div className="mt-1 text-xs text-gray-600">{formatRoleLabel(employee.employeeRole)}</div>
                <div className="mt-1 text-xs text-gray-500">{buildAreaLabels(employee.employeeAreas).join(', ')}</div>
              </td>
              {employee.days.map((dayStatus) => {
                const isToday = getDateKey(dayStatus.date) === todayKey;
                return (
                  <td key={getDateKey(dayStatus.date)} className={`px-3 py-4 align-middle ${isToday ? 'bg-sky-50/60' : ''}`}>
                    <ScheduleDayCell dayStatus={dayStatus} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const formatDateOnly = (value) => {
  const key = getDateKey(value);
  return key ? format(new Date(`${key}T00:00:00`), 'MMM d, yyyy') : '';
};

function PtoOverviewView({ items, onEditAvailability, isAdmin }) {
  const canEditAvailability = typeof onEditAvailability === 'function';

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] table-fixed">
        <colgroup>
          <col className={isAdmin ? 'w-[25%]' : 'w-[40%]'} />
          <col className={isAdmin ? 'w-[27%]' : 'w-[60%]'} />
          {isAdmin && <col className="w-[18%]" />}
          {isAdmin && <col className="w-[15%]" />}
          {canEditAvailability && <col className="w-[15%]" />}
        </colgroup>
        <thead className="border-b border-gray-200 bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Team Member</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Dates</th>
            {isAdmin && (
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">PTO Remaining</th>
            )}
            {isAdmin && (
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">PTO Base</th>
            )}
            {canEditAvailability && (
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Action</th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const hasVacation = Boolean(item.vacationId);
            const statusLabel = item.isCurrent
              ? `${item.daysRemaining} ${item.daysRemaining === 1 ? 'day' : 'days'} left`
              : hasVacation
                ? item.daysUntilStart === 0
                  ? 'Starts today'
                  : `Starts in ${item.daysUntilStart} ${item.daysUntilStart === 1 ? 'day' : 'days'}`
                : 'No PTO scheduled';
            const statusClasses = item.isCurrent
              ? 'border-violet-200 bg-violet-50 text-violet-800'
              : hasVacation
                ? 'border-sky-200 bg-sky-50 text-sky-800'
                : 'border-gray-200 bg-gray-100 text-gray-600';

            return (
              <tr key={item.employeeId} className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50">
                <td className="px-4 py-4 font-semibold text-gray-900">{item.employeeName}</td>
                <td className="px-4 py-4 text-sm text-gray-700">
                  <div className="font-medium text-gray-900">
                    {hasVacation
                      ? `${formatDateOnly(item.startDate)} – ${formatDateOnly(item.endDate)}`
                      : 'No PTO scheduled'}
                  </div>
                  {hasVacation && (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClasses}`}>
                        {statusLabel}
                      </span>
                      <span className="text-xs font-medium text-gray-600">
                        {item.vacationType || 'Vacation'}
                      </span>
                      {item.approvalStatus === 'PENDING' && (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                          Pending
                        </span>
                      )}
                    </div>
                  )}
                </td>
                {isAdmin && (
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                      item.ptoDaysRemaining == null
                        ? 'border-gray-200 bg-gray-100 text-gray-600'
                        : 'border-violet-200 bg-violet-50 text-violet-800'
                    }`}>
                      {item.ptoDaysRemaining == null
                        ? 'Not Set'
                        : `${item.ptoDaysRemaining} ${item.ptoDaysRemaining === 1 ? 'day' : 'days'}`}
                    </span>
                  </td>
                )}
                {isAdmin && (
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                      item.paidTimeOffDays == null
                        ? 'border-gray-200 bg-gray-100 text-gray-600'
                        : 'border-sky-200 bg-sky-50 text-sky-800'
                    }`}>
                      {item.paidTimeOffDays == null
                        ? 'Not Set'
                        : `${item.paidTimeOffDays} ${item.paidTimeOffDays === 1 ? 'day' : 'days'}`}
                    </span>
                  </td>
                )}
                {canEditAvailability && (
                  <td className="px-4 py-4 text-right">
                    <button
                      type="button"
                      onClick={() => onEditAvailability(item)}
                      className="inline-flex items-center justify-center gap-1.5 rounded-md border border-sky-600 bg-sky-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1 whitespace-nowrap"
                      aria-label={`Edit availability for ${item.employeeName}`}
                    >
                      <svg
                        className="h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit Availability
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TeamStatusAccordionView({
  viewMode = 'list',
  canManageAvailability = false,
  initialUserId = null,
  initialRequestId = null,
}) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'ADMIN';
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  );
  const [expandedId, setExpandedId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedVacationUserId, setSelectedVacationUserId] = useState(null);
  const [isVacationModalOpen, setIsVacationModalOpen] = useState(false);
  const [adminEditEmployee, setAdminEditEmployee] = useState(null);
  const [isAdminEditOpen, setIsAdminEditOpen] = useState(false);
  const [activeRequestId, setActiveRequestId] = useState(initialRequestId);
  const [filters, setFilters] = useState({ searchTerm: '', limit: 20, offset: 0 });
  const [workingStatusFilter, setWorkingStatusFilter] = useState('ALL');
  const [selectedRoles, setSelectedRoles] = useState(DEFAULT_SELECTED_ROLES);
  // Default area filters to the signed-in user's assigned region.
  // No selected area pills means no area filter, so all areas are shown.
  const [selectedAreaGroups, setSelectedAreaGroups] = useState(() =>
    deriveDefaultAreaGroups(currentUser?.areas),
  );
  const hasInitializedAreaGroups = React.useRef(false);
  const selectedAreas = useMemo(() => {
    return selectedAreaGroups.flatMap((areaGroup) => AREA_GROUPS[areaGroup]?.areas || []);
  }, [selectedAreaGroups]);

  const weekEnd = useMemo(
    () => endOfWeek(currentWeekStart, { weekStartsOn: 1 }),
    [currentWeekStart],
  );
  const weekDays = useMemo(
    () => eachDayOfInterval({ start: currentWeekStart, end: weekEnd }),
    [currentWeekStart, weekEnd],
  );
  const isViewingCurrentWeek = getDateKey(currentWeekStart) === getDateKey(
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  );

  const queryFilters = useMemo(() => {
    const filterObj = { limit: filters.limit, offset: filters.offset };
    filterObj.roles = selectedRoles;
    if (filters.searchTerm && filters.searchTerm.trim()) {
      filterObj.searchTerm = filters.searchTerm.trim();
    }
    if (selectedAreas.length > 0) filterObj.areas = selectedAreas;
    if (viewMode === 'list' && workingStatusFilter !== 'ALL') {
      filterObj.workingStatus = workingStatusFilter;
    }
    return filterObj;
  }, [filters.limit, filters.offset, filters.searchTerm, selectedRoles, selectedAreas, viewMode, workingStatusFilter]);

  const { data: usersData } = useQuery(GET_USERS, {
    fetchPolicy: 'cache-first',
    skip: !canManageAvailability,
  });

  const {
    data,
    previousData: previousTapeChartData,
    loading,
    error,
    refetch: refetchTapeChart,
  } = useQuery(
    GET_TEAM_AVAILABILITY_TAPE_CHART,
    {
      variables: {
        startDate: `${format(currentWeekStart, 'yyyy-MM-dd')}T00:00:00.000Z`,
        endDate: `${format(weekEnd, 'yyyy-MM-dd')}T23:59:59.999Z`,
        filters: queryFilters,
      },
      fetchPolicy: 'network-only',
      skip: viewMode === 'pto',
    },
  );

  const {
    data: ptoData,
    previousData: previousPtoData,
    loading: ptoLoading,
    error: ptoError,
  } = useQuery(GET_TEAM_PTO_OVERVIEW, {
    variables: { filters: queryFilters },
    fetchPolicy: 'network-only',
    skip: viewMode !== 'pto',
  });

  React.useEffect(() => {
    if (!currentUser || hasInitializedAreaGroups.current) return;
    setSelectedAreaGroups(deriveDefaultAreaGroups(currentUser.areas));
    hasInitializedAreaGroups.current = true;
  }, [currentUser]);

  React.useEffect(() => {
    setFilters((prev) => ({ ...prev, offset: 0 }));
    setExpandedId(null);
  }, [viewMode]);

  React.useEffect(() => {
    if (!isAdmin || !initialUserId || !usersData?.users) return;
    const linkedUser = usersData.users.find((user) => Number(user.id) === Number(initialUserId));
    if (!linkedUser) return;
    setAdminEditEmployee({
      id: linkedUser.id,
      name: linkedUser.name,
      role: linkedUser.role,
    });
    setActiveRequestId(initialRequestId);
    setIsAdminEditOpen(true);
  }, [initialRequestId, initialUserId, isAdmin, usersData]);

  const tapeChartData = data?.teamAvailabilityTapeChart
    || previousTapeChartData?.teamAvailabilityTapeChart;
  const employees = useMemo(() => tapeChartData?.items || [], [tapeChartData?.items]);
  const ptoOverviewData = ptoData?.teamPtoOverview || previousPtoData?.teamPtoOverview;
  const ptoItems = useMemo(() => ptoOverviewData?.items || [], [ptoOverviewData?.items]);
  const activeLoading = viewMode === 'pto'
    ? ptoLoading && !ptoOverviewData
    : loading && !tapeChartData;
  const activeError = viewMode === 'pto' ? ptoError : error;
  const total = viewMode === 'pto' ? ptoOverviewData?.total || 0 : tapeChartData?.total || 0;
  const hasMore = viewMode === 'pto' ? ptoOverviewData?.hasMore || false : tapeChartData?.hasMore || false;
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / filters.limit));

  const handlePreviousWeek = () => {
    setCurrentWeekStart(subWeeks(currentWeekStart, 1));
    setWorkingStatusFilter('ALL');
    setFilters((prev) => ({ ...prev, offset: 0 }));
    setExpandedId(null);
  };
  const handleNextWeek = () => {
    setCurrentWeekStart(addWeeks(currentWeekStart, 1));
    setWorkingStatusFilter('ALL');
    setFilters((prev) => ({ ...prev, offset: 0 }));
    setExpandedId(null);
  };
  const handleExpandedPreviousWeek = () => {
    setCurrentWeekStart((previousWeekStart) => subWeeks(previousWeekStart, 1));
    setWorkingStatusFilter('ALL');
    setFilters((prev) => ({ ...prev, offset: 0 }));
  };
  const handleExpandedNextWeek = () => {
    setCurrentWeekStart((previousWeekStart) => addWeeks(previousWeekStart, 1));
    setWorkingStatusFilter('ALL');
    setFilters((prev) => ({ ...prev, offset: 0 }));
  };
  const handleToday = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    setFilters((prev) => ({ ...prev, offset: 0 }));
    setExpandedId(null);
  };
  const handleWorkingStatusFilterChange = (status) => {
    setWorkingStatusFilter(status);
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    setFilters((prev) => ({ ...prev, offset: 0 }));
    setExpandedId(null);
  };
  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, searchTerm: e.target.value, offset: 0 }));
    setExpandedId(null);
  };
  const handleRoleToggle = (roleValue) => {
    setSelectedRoles((prev) => {
      if (prev.includes(roleValue)) {
        return prev.filter((role) => role !== roleValue);
      }

      return [...prev, roleValue];
    });
    setFilters((prev) => ({ ...prev, offset: 0 }));
    setExpandedId(null);
  };
  const handleAreaGroupToggle = (areaGroupValue) => {
    setSelectedAreaGroups((prev) => {
      if (prev.includes(areaGroupValue)) {
        return prev.filter((areaGroup) => areaGroup !== areaGroupValue);
      }

      return [...prev, areaGroupValue];
    });
    setFilters((prev) => ({ ...prev, offset: 0 }));
    setExpandedId(null);
  };
  const handlePreviousPage = () => {
    setFilters((prev) => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    setExpandedId(null);
  };
  const handleNextPage = () => {
    if (!hasMore) return;
    setFilters((prev) => ({ ...prev, offset: prev.offset + prev.limit }));
    setExpandedId(null);
  };
  const handleToggleRow = (employeeId) => {
    setExpandedId((prev) => (prev === employeeId ? null : employeeId));
  };
  const handleEditWeek = (employeeId) => {
    if (!canManageAvailability) return;
    setSelectedUserId(employeeId);
    setIsEditModalOpen(true);
  };
  const handleEditOffDates = (employeeId) => {
    if (!canManageAvailability) return;
    setSelectedVacationUserId(employeeId);
    setIsVacationModalOpen(true);
  };
  const handleEditAvailability = (employee) => {
    if (!isAdmin) return;
    setActiveRequestId(null);
    setAdminEditEmployee({
      id: employee.employeeId,
      name: employee.employeeName,
      role: employee.employeeRole,
    });
    setIsAdminEditOpen(true);
  };
  const handleCloseAdminEdit = () => {
    setIsAdminEditOpen(false);
    setAdminEditEmployee(null);
    setActiveRequestId(null);
  };
  const handleAdminDataChanged = () => {
    refetchTapeChart();
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {viewMode === 'pto' || isViewingCurrentWeek ? 'Today' : 'Week of:'}
            </div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {viewMode === 'pto' || isViewingCurrentWeek
                ? format(new Date(), 'EEEE MMMM d, yyyy')
                : format(currentWeekStart, 'EEEE MMMM d, yyyy')}
            </div>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-5">
            {viewMode !== 'pto' && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 shadow-sm">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Week of:
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousWeek}
                    className="p-2 rounded-md border border-gray-200 bg-white hover:bg-gray-100 transition-colors"
                    aria-label="Previous week"
                  >
                    <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="font-medium text-gray-700 min-w-[200px] text-center">
                    {format(currentWeekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
                  </span>
                  <button
                    onClick={handleNextWeek}
                    className="p-2 rounded-md border border-gray-200 bg-white hover:bg-gray-100 transition-colors"
                    aria-label="Next week"
                  >
                    <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={handleToday}
                    className="px-3 py-2 rounded-md border border-gray-200 text-sm font-medium bg-white text-gray-700 hover:bg-gray-100 transition-colors ml-1"
                  >
                    Today
                  </button>
                </div>
              </div>
            )}
            <div className="inline-flex max-w-md items-start gap-2 rounded-md border border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-800">
              <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                {viewMode === 'list' && 'Open a team member to see their full work week.'}
                {viewMode === 'calendar' && 'Review each team member’s full schedule across the selected week.'}
                {viewMode === 'pto' && 'Current time off appears first, followed by the nearest upcoming requests.'}
              </span>
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label htmlFor="teamStatusSearch" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              id="teamStatusSearch"
              type="text"
              placeholder="Search employees..."
              value={filters.searchTerm}
              onChange={handleSearchChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
            />
          </div>
          <FilterDropdown
            label="Areas"
            options={AREA_GROUP_OPTIONS}
            selectedValues={selectedAreaGroups}
            onToggle={handleAreaGroupToggle}
            onClear={() => {
              setSelectedAreaGroups([]);
              setFilters((prev) => ({ ...prev, offset: 0 }));
              setExpandedId(null);
            }}
          />
          <FilterDropdown
            label="Roles"
            options={AVAILABLE_ROLES}
            selectedValues={selectedRoles}
            onToggle={handleRoleToggle}
            onClear={() => {
              setSelectedRoles([]);
              setFilters((prev) => ({ ...prev, offset: 0 }));
              setExpandedId(null);
            }}
          />
        </div>
        {!activeLoading && (total > 0 || (viewMode === 'list' && workingStatusFilter !== 'ALL')) && (
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>
              {total > 0
                ? `Showing ${filters.offset + 1} - ${Math.min(filters.offset + filters.limit, total)} of ${total} employees`
                : 'No employees match this status'}
            </span>
            {viewMode === 'list' && (
              <div
                className="inline-flex rounded-lg border border-gray-300 bg-gray-100 p-0.5 shadow-sm"
                role="group"
                aria-label="Filter by working status"
              >
                {[
                  { value: 'ALL', label: 'All Status' },
                  { value: 'WORKING', label: 'Working' },
                  { value: 'OFF', label: 'Off' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleWorkingStatusFilterChange(option.value)}
                    aria-pressed={workingStatusFilter === option.value}
                    className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                      workingStatusFilter === option.value
                        ? 'bg-sky-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-sky-50 hover:text-sky-800'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {activeLoading ? (
        <div className="p-8 text-center text-gray-500">Loading...</div>
      ) : activeError ? (
        <div className="p-8 text-center text-red-500">Error: {activeError.message}</div>
      ) : viewMode === 'pto' && ptoItems.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No team members found</div>
      ) : viewMode !== 'pto' && employees.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No employees found</div>
      ) : viewMode === 'pto' ? (
        <PtoOverviewView
          items={ptoItems}
          onEditAvailability={isAdmin ? handleEditAvailability : null}
          isAdmin={isAdmin}
        />
      ) : viewMode === 'calendar' ? (
        <CalendarAvailabilityView employees={employees} weekDays={weekDays} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] table-fixed">
            <colgroup>
              <col className="w-[20%]" />
              <col className="w-[17%]" />
              <col className="w-[22%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[14%]" />
              <col className="w-[3%]" />
            </colgroup>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Team Member</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Area</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">Schedule Set</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">Location Set</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Working Status</th>
                <th className="px-4 py-3"><span className="sr-only">Expand</span></th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => (
                <EmployeeRow
                  key={employee.employeeId}
                  employee={employee}
                  weekDays={weekDays}
                  isExpanded={expandedId === employee.employeeId}
                  onToggle={() => handleToggleRow(employee.employeeId)}
                  onEditWeek={() => handleEditWeek(employee.employeeId)}
                  onEditOffDates={() => handleEditOffDates(employee.employeeId)}
                  onEditAvailability={() => handleEditAvailability(employee)}
                  onPreviousWeek={handleExpandedPreviousWeek}
                  onNextWeek={handleExpandedNextWeek}
                  canManageAvailability={canManageAvailability}
                  isAdmin={isAdmin}
                  canViewPtoBalance={isAdmin || Number(employee.employeeId) === Number(currentUser?.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!activeLoading && !activeError && total > 0 && (
        <div className="flex items-center justify-center gap-3 border-t border-gray-200 bg-gray-50 p-4">
          <button
            onClick={handlePreviousPage}
            disabled={filters.offset === 0}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              filters.offset === 0
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!hasMore}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              !hasMore
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Next
          </button>
        </div>
      )}

      {viewMode !== 'pto' && <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="font-medium text-gray-700">Schedule legend:</span>
          <div className="flex items-center gap-2">
            <div className={`w-4 h-4 ${SCHEDULE_COLORS.WORKING} rounded`}></div>
            <span>On-Island</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-4 h-4 ${SCHEDULE_COLORS.WORKING_REMOTE} rounded`}></div>
            <span>Remote</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-4 h-4 ${SCHEDULE_COLORS.OFF} rounded border border-gray-300`}></div>
            <span>Off</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-4 h-4 ${SCHEDULE_COLORS.NOT_SET} rounded`}></div>
            <span>Not Set</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-4 h-4 ${SCHEDULE_COLORS.HOLIDAY} rounded`}></div>
            <span>Holiday</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-4 h-4 ${SCHEDULE_COLORS.VACATION} rounded`}></div>
            <span>Vacation</span>
          </div>
        </div>
      </div>}

      {canManageAvailability && isEditModalOpen && selectedUserId && (
        <EditScheduleModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedUserId(null);
          }}
          userId={selectedUserId}
          onSaved={() => {
            refetchTapeChart();
            setIsEditModalOpen(false);
            setSelectedUserId(null);
          }}
        />
      )}

      {canManageAvailability && isVacationModalOpen && selectedVacationUserId && usersData?.users && (
        <VacationFormModal
          isOpen={isVacationModalOpen}
          onClose={() => {
            setIsVacationModalOpen(false);
            setSelectedVacationUserId(null);
          }}
          userId={selectedVacationUserId}
          users={usersData.users}
          vacation={null}
          onSaved={() => {
            refetchTapeChart();
            setIsVacationModalOpen(false);
            setSelectedVacationUserId(null);
          }}
        />
      )}

      {isAdmin && (
        <AdminEditUserAvailabilityPanel
          isOpen={isAdminEditOpen}
          onClose={handleCloseAdminEdit}
          userId={adminEditEmployee?.id}
          userName={adminEditEmployee?.name}
          userRole={adminEditEmployee?.role}
          requestId={activeRequestId}
          onDataChanged={handleAdminDataChanged}
        />
      )}
    </div>
  );
}

export default TeamStatusAccordionView;
